import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, Button, Stack, Pagination, FormControl, Select, MenuItem,
  Alert, Tabs, Tab, Grid, Card, CardContent, Switch, Divider, TextField,
  IconButton, alpha, Avatar, CircularProgress,
} from '@mui/material';
import {
  Visibility, Receipt, Refresh, Settings, Payment, CheckCircle, Warning,
  AccountBalance, CreditCard, Shield, TrendingUp,
} from '@mui/icons-material';
import { adminAPI, settingsAPI } from '../../api/client';
import { integerOnly, decimalOnly } from '../../utils/inputHelpers';

const statusColors = { pending: 'warning', processing: 'info', completed: 'success', failed: 'error', refunded: 'default', partially_refunded: 'secondary' };
const typeColors = { rent: 'primary', deposit: 'secondary', service_fee: 'info', refund: 'warning', penalty: 'error' };

function AdminPaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [ps, setPs] = useState({
    platform_fee_percent: '5', payment_gateway: 'paystack',
    auto_payout: 'true', payout_delay_days: '3',
    min_payout_amount: '50', max_transaction: '100000',
    fraud_detection: 'true', fraud_threshold: '10000',
    allow_mobile_money: 'true', allow_card: 'true', allow_bank_transfer: 'true',
    refund_processing_days: '7',
  });

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 15 };
      if (statusFilter) params.status = statusFilter;
      const res = await adminAPI.listPayments(params);
      setPayments(res.data.payments || []);
      setTotal(res.data.total || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPayments(); }, [page, statusFilter]);
  useEffect(() => {
    settingsAPI.list({ category: 'payment' }).then(res => {
      if (Array.isArray(res.data)) {
        const m = {}; res.data.forEach(s => { m[s.key] = s.value; });
        setPs(p => ({ ...p, ...m }));
      }
    }).catch(() => {});
  }, []);

  const saveSetting = async (key, value) => {
    try {
      await settingsAPI.update(key, { value: String(value) });
      setPs(p => ({ ...p, [key]: String(value) }));
      setSuccess('Setting updated');
    } catch (_) { setError('Failed to update'); }
  };

  const completedTotal = payments.filter(p => p.status === 'completed').reduce((s, p) => s + (p.amount || 0), 0);

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" fontWeight={800} sx={{ background: 'linear-gradient(135deg, #10b981, #34d399)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Payment Management
          </Typography>
          <Typography variant="body2" color="text.secondary">Monitor transactions, payouts, and financial settings</Typography>
        </Box>
        <IconButton onClick={fetchPayments} sx={{ bgcolor: 'white', boxShadow: 1 }}><Refresh /></IconButton>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper sx={{ borderRadius: 3, mb: 3, px: 1 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ '& .MuiTabs-indicator': { height: 3, borderRadius: 2 } }}>
          <Tab label="Transactions" icon={<Payment sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Gateway & Fees" icon={<CreditCard sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Payouts & Fraud" icon={<Shield sx={{ fontSize: 18 }} />} iconPosition="start" />
        </Tabs>
      </Paper>

      {tabValue === 0 && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Total Transactions', count: total, color: '#6366f1', icon: <Payment /> },
              { label: 'Completed', count: payments.filter(p => p.status === 'completed').length, color: '#10b981', icon: <CheckCircle /> },
              { label: 'Pending', count: payments.filter(p => p.status === 'pending').length, color: '#f59e0b', icon: <Warning /> },
              { label: 'Revenue', count: `GH₵${completedTotal.toLocaleString()}`, color: '#3b82f6', icon: <TrendingUp /> },
            ].map((item, i) => (
              <Grid item xs={6} sm={3} key={i}>
                <Card sx={{ borderLeft: `4px solid ${item.color}` }}>
                  <CardContent sx={{ py: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar sx={{ bgcolor: alpha(item.color, 0.1), color: item.color, width: 36, height: 36 }}>{item.icon}</Avatar>
                      <Box><Typography variant="body2" color="text.secondary">{item.label}</Typography><Typography variant="h6" fontWeight={800}>{item.count}</Typography></Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select value={statusFilter} displayEmpty onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
                <MenuItem value="">All Statuses</MenuItem>
                {['pending','processing','completed','failed','refunded','partially_refunded'].map(s =>
                  <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s.replace('_',' ')}</MenuItem>)}
              </Select>
            </FormControl>
          </Paper>

          <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Transaction</TableCell><TableCell>User</TableCell><TableCell>Type</TableCell>
                <TableCell>Amount</TableCell><TableCell>Method</TableCell><TableCell>Status</TableCell>
                <TableCell>Date</TableCell><TableCell>Actions</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {loading ? <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
                : payments.length === 0 ? <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}>No payments found</TableCell></TableRow>
                : payments.map((p) => (
                  <TableRow key={p.id} hover>
                    <TableCell><Typography fontWeight={600} variant="body2" sx={{ fontFamily: 'monospace' }}>{p.transaction_ref?.slice(0,12)}...</Typography></TableCell>
                    <TableCell><Typography variant="body2" fontWeight={600}>{p.user?.first_name} {p.user?.last_name}</Typography></TableCell>
                    <TableCell><Chip label={p.payment_type?.replace('_',' ')} size="small" color={typeColors[p.payment_type]||'default'} sx={{ textTransform: 'capitalize' }} /></TableCell>
                    <TableCell>
                      <Typography fontWeight={600} variant="body2">{p.currency} {p.amount?.toLocaleString()}</Typography>
                      {p.refund_amount > 0 && <Typography variant="caption" color="error">Refund: {p.currency} {p.refund_amount?.toLocaleString()}</Typography>}
                    </TableCell>
                    <TableCell><Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{p.payment_method?.replace('_',' ')||'N/A'}</Typography></TableCell>
                    <TableCell><Chip label={p.status?.replace('_',' ')} size="small" color={statusColors[p.status]||'default'} sx={{ textTransform: 'capitalize' }} /></TableCell>
                    <TableCell><Typography variant="body2">{new Date(p.created_at).toLocaleDateString()}</Typography></TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        {p.receipt_url && <IconButton size="small" href={p.receipt_url} target="_blank"><Receipt fontSize="small" /></IconButton>}
                        <IconButton size="small" href={`/dashboard/payments/${p.id}`} target="_blank"><Visibility fontSize="small" /></IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {total > 15 && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}><Pagination count={Math.ceil(total/15)} page={page} onChange={(e,v)=>setPage(v)} color="primary" /></Box>}
        </>
      )}

      {tabValue === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Payment Gateway</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                <Box><Typography fontWeight={600} gutterBottom>Active Gateway</Typography>
                  <FormControl size="small" fullWidth>
                    <Select value={ps.payment_gateway} onChange={(e) => saveSetting('payment_gateway', e.target.value)}>
                      <MenuItem value="paystack">Paystack (Local - GHS)</MenuItem>
                      <MenuItem value="stripe">Stripe (International - USD)</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                {[
                  { key: 'allow_mobile_money', label: 'Mobile Money', desc: 'MTN, Vodafone, AirtelTigo' },
                  { key: 'allow_card', label: 'Card Payments', desc: 'Visa, Mastercard' },
                  { key: 'allow_bank_transfer', label: 'Bank Transfer', desc: 'Direct bank transfers' },
                ].map(item => (
                  <Stack key={item.key} direction="row" justifyContent="space-between" alignItems="center">
                    <Box><Typography fontWeight={600}>{item.label}</Typography><Typography variant="body2" color="text.secondary">{item.desc}</Typography></Box>
                    <Switch checked={ps[item.key]==='true'} onChange={(e)=>saveSetting(item.key, e.target.checked?'true':'false')} />
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Fees & Limits</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                {[
                  { key: 'platform_fee_percent', label: 'Platform Fee (%)' },
                  { key: 'max_transaction', label: 'Max Transaction (GH₵)' },
                  { key: 'refund_processing_days', label: 'Refund Processing (days)' },
                ].map(item => (
                  <Box key={item.key}><Typography fontWeight={600} gutterBottom>{item.label}</Typography>
                    <TextField size="small" type="number" value={ps[item.key]}
                      onChange={(e)=>setPs(p=>({...p,[item.key]:e.target.value}))}
                      onKeyDown={integerOnly}
                      onBlur={(e)=>saveSetting(item.key, e.target.value)} sx={{ width: 140 }} />
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      {tabValue === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Landlord Payouts</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box><Typography fontWeight={600}>Auto Payout</Typography><Typography variant="body2" color="text.secondary">Automatically pay landlords after delay</Typography></Box>
                  <Switch checked={ps.auto_payout==='true'} onChange={(e)=>saveSetting('auto_payout', e.target.checked?'true':'false')} />
                </Stack>
                {[
                  { key: 'payout_delay_days', label: 'Payout Delay (days)' },
                  { key: 'min_payout_amount', label: 'Min Payout Amount (GH₵)' },
                ].map(item => (
                  <Box key={item.key}><Typography fontWeight={600} gutterBottom>{item.label}</Typography>
                    <TextField size="small" type="number" value={ps[item.key]}
                      onChange={(e)=>setPs(p=>({...p,[item.key]:e.target.value}))}
                      onKeyDown={decimalOnly}
                      onBlur={(e)=>saveSetting(item.key, e.target.value)} sx={{ width: 120 }} />
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Fraud Detection</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box><Typography fontWeight={600}>Enable Fraud Detection</Typography><Typography variant="body2" color="text.secondary">Flag suspicious transactions</Typography></Box>
                  <Switch checked={ps.fraud_detection==='true'} onChange={(e)=>saveSetting('fraud_detection', e.target.checked?'true':'false')} />
                </Stack>
                <Box><Typography fontWeight={600} gutterBottom>Fraud Alert Threshold (GH₵)</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Flag transactions above this amount</Typography>
                  <TextField size="small" type="number" value={ps.fraud_threshold}
                    onChange={(e)=>setPs(p=>({...p,fraud_threshold:e.target.value}))}
                    onKeyDown={decimalOnly}
                    onBlur={(e)=>saveSetting('fraud_threshold', e.target.value)} sx={{ width: 140 }} />
                </Box>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography fontWeight={600} gutterBottom>Fraud Checks</Typography>
                  <Stack spacing={1}>
                    {['Duplicate transaction detection','Velocity checks','Amount anomaly detection','IP geolocation mismatch','Card BIN verification'].map((c,i) => (
                      <Stack key={i} direction="row" alignItems="center" spacing={1}>
                        <CheckCircle color="success" sx={{ fontSize: 16 }} />
                        <Typography variant="body2">{c}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Paper>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

export default AdminPaymentsPage;
